/**
 * Client for /api/analytics/v2. Every request carries the Firebase ID token as a bearer header; the token is never stored,
 * logged or put in a URL. Downloads go through fetch for the same reason (an <a href> cannot send the header).
 */
import { t, tx } from '../i18n/core';
import type { MessageKey } from '../i18n/messages';

export class V2Error extends Error {
  constructor(public readonly status: number, message: string) { super(message); }
}

export type Query = Record<string, string | number | undefined | null>;

export interface V2Transport {
  get<T>(path: string, query?: Query, signal?: AbortSignal): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  download(path: string): Promise<{ blob: Blob; filename: string }>;
}

const MESSAGES: Record<number, MessageKey> = {
  401: 'common.error.401',
  403: 'common.error.403',
  404: 'common.error.404',
  429: 'common.error.429',
};

export class V2Client implements V2Transport {
  constructor(private readonly baseUrl: string, private readonly getAccessToken: () => Promise<string | null>) {}

  private url(path: string, query: Query = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    const search = params.toString();
    return `${this.baseUrl}${path}${search ? `?${search}` : ''}`;
  }

  private async headers(extra: Record<string, string> = {}) {
    const token = await this.getAccessToken();
    return { Accept: 'application/json', ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }

  private async fail(response: Response): Promise<never> {
    let title: string | undefined;
    try { title = ((await response.json()) as { title?: string }).title; } catch { /* not JSON */ }
    // A 400 or 409 title explains what to fix; for the others a fixed message is clearer than the server's.
    const known = MESSAGES[response.status];
    const message = (response.status === 400 || response.status === 409) && title ? tx(title) : known ? t(known) : title ? tx(title) : t('common.error.failed');
    throw new V2Error(response.status, message);
  }

  async get<T>(path: string, query?: Query, signal?: AbortSignal): Promise<T> {
    const response = await fetch(this.url(path, query), { signal, credentials: 'include', headers: await this.headers() });
    if (!response.ok) return this.fail(response);
    return response.json() as Promise<T>;
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(this.url(path), { method: 'POST', credentials: 'include', headers: await this.headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(body) });
    if (!response.ok) return this.fail(response);
    return response.json() as Promise<T>;
  }

  async download(path: string): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(this.url(path), { credentials: 'include', headers: await this.headers({ Accept: '*/*' }) });
    if (!response.ok) return this.fail(response);
    return { blob: await response.blob(), filename: filenameFrom(response.headers.get('Content-Disposition')) ?? 'aruha-export' };
  }
}

export function filenameFrom(disposition: string | null): string | null {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match ? match[1]! : null;
}

/** Hands a downloaded file to the browser. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
